import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { DebugInfo } from "@/components/_molecules/debug-info";
import { Navigate } from "@/components/_molecules/navigation-controls/navigate";
import { useGetUser2FAStatus } from "@/domain/two-factor-auth/hooks/api/use-get-user-2fa-status";
import { AppearanceProvider } from "@/domain/user/components/appearance-context";
import { CurrentUserContextProvider, useCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";

const TwoFactorSetupGuard = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const { currentUser } = useCurrentUserContext();
  const { data: twoFactorAuthStatus, isLoading: isLoading2FA } = useGetUser2FAStatus();
  const [isChecking, setIsChecking] = useState(true);

  const workspaceRequires2FA = useMemo(() => {
    if (!currentUser?.workspaceMembershipList) {
      return false;
    }
    return currentUser.workspaceMembershipList.some(
      (membership) => membership.status === "ACTIVE" && membership.workspace?.twoFactorAuth?.isRequired === true,
    );
  }, [currentUser]);

  const is2FAEnabled = twoFactorAuthStatus?.isEnabled ?? false;
  const isOnSetupPage = location.pathname === "/auth/setup-2fa";

  useEffect(() => {
    if (!isLoading2FA) {
      setIsChecking(false);
    }
  }, [isLoading2FA]);

  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">Laden...</div>
      </div>
    );
  }

  // If workspace requires 2FA but user hasn't enabled it, redirect to setup (unless already on setup page)
  if (currentUser && workspaceRequires2FA && !is2FAEnabled && !isOnSetupPage) {
    const redirectUrl = `/auth/setup-2fa?redirect=${encodeURIComponent(location.pathname + location.search)}`;
    return <Navigate to={redirectUrl} replace />;
  }

  return <>{children}</>;
};

export const ProtectedRoute = ({ isAuthenticated, children }: { isAuthenticated: boolean; children: JSX.Element }) => {
  const location = useLocation();

  if (!isAuthenticated) {
    const redirectUrl = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
    return <Navigate to={redirectUrl} replace />;
  }

  // ensure all routes within a protected route have access to the current user
  return (
    <CurrentUserContextProvider>
      <AppearanceProvider>
        <TwoFactorSetupGuard>
          <>
            {children}
            <DebugInfo />
          </>
        </TwoFactorSetupGuard>
      </AppearanceProvider>
    </CurrentUserContextProvider>
  );
};
